import React, { useCallback, useEffect, useRef } from "react"
import { BrackyAvatarImage } from "./components/BrackyAvatarImage"
import { useBrackyAnimations } from "./hooks/use-bracky-animations"
import { useBrackyEvents } from "./hooks/use-bracky-events"

type AnimationState = {
  playDoubleNod: () => void
  playWink3D: (persistent?: boolean) => void
  playShocked: (persistent?: boolean) => void
  playSideEye: (persistent?: boolean) => void
  resetAnimations: () => void
  playWince: (pressed?: boolean) => void
}

export const brackyAnimations: AnimationState = {
  playDoubleNod: () => {
    // No-op: Animation disabled in static site
  },
  playWink3D: () => {
    // No-op: Animation disabled in static site
  },
  playShocked: () => {
    // No-op: Animation disabled in static site
  },
  playSideEye: () => {
    // No-op: Animation disabled in static site
  },
  resetAnimations: () => {
    // No-op: Animation disabled in static site
  },
  playWince: () => {
    // No-op: Animation disabled in static site
  },
}

const BrackyAvatar = React.memo(function BrackyAvatar() {
  const { state, actions, utils } = useBrackyAnimations()
  const initialWinkTriggeredRef = useRef(false)
  const longPressTimerRef = useRef<NodeJS.Timeout | null>(null)

  useEffect(() => {
    if (!initialWinkTriggeredRef.current) {
      initialWinkTriggeredRef.current = true
      actions.triggerInitialWink()
    }

    brackyAnimations.playDoubleNod = actions.playDoubleNod
    brackyAnimations.playWink3D = actions.playWink3D
    brackyAnimations.playShocked = actions.playShocked
    brackyAnimations.playSideEye = actions.playSideEye
    brackyAnimations.resetAnimations = actions.resetAnimations
    brackyAnimations.playWince = actions.playWince

    return () => {
      brackyAnimations.playDoubleNod = () => {
        // Cleanup: no-op function
      }
      brackyAnimations.playWink3D = () => {
        // Cleanup: no-op function
      }
      brackyAnimations.playShocked = () => {
        // Cleanup: no-op function
      }
      brackyAnimations.playSideEye = () => {
        // Cleanup: no-op function
      }
      brackyAnimations.resetAnimations = () => {
        // Cleanup: no-op function
      }
      brackyAnimations.playWince = () => {
        // Cleanup: no-op function
      }

      utils.clearAllTimers()
    }
  }, [actions, utils])

  useBrackyEvents({
    playWink3D: actions.playWink3D,
    playSideEye: actions.playSideEye,
    playWince: actions.playWince,
  })

  const handleBrackyClick = useCallback(() => {
    actions.playDoubleNod()
    if (typeof window !== "undefined") {
      window.dispatchEvent(new Event("brackyAvatarClick"))
    }
  }, [actions])

  const startLongPressAnimation = useCallback(() => {
    longPressTimerRef.current = setTimeout(() => {
      actions.playSideEye(false)

      setTimeout(() => {
        actions.setIsBrackyPressed(false)
        actions.setIsBrackyHovered(false)

        setTimeout(() => {
          initialWinkTriggeredRef.current = true
        }, 50)
      }, 2000)
    }, 1000) as unknown as NodeJS.Timeout
  }, [actions])

  const handleMouseUp = useCallback(() => {
    if (longPressTimerRef.current) {
      clearTimeout(longPressTimerRef.current)
      longPressTimerRef.current = null
    }

    if (!utils.isAnimationActive()) {
      actions.setIsBrackyPressed(false)
      actions.setShowWinkAnimation(false)
    }
  }, [actions, utils])

  const handleMouseDown = useCallback(() => {
    if (!utils.isAnimationActive()) {
      actions.setIsBrackyPressed(true)
      startLongPressAnimation()
    }
  }, [actions, utils, startLongPressAnimation])

  const handleMouseLeave = useCallback(() => {
    handleMouseUp()
    if (!utils.isAnimationActive()) {
      actions.setIsBrackyHovered(false)
    }
  }, [handleMouseUp, actions, utils])

  const handleMouseEnter = useCallback(() => {
    if (!("ontouchstart" in window || utils.isAnimationActive())) {
      actions.setIsBrackyHovered(true)
    }
  }, [actions, utils])

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault()
        handleBrackyClick()
      }
    },
    [handleBrackyClick],
  )

  return (
    <div className="relative z-20 mr-[.18rem] translate-y-[.17rem]">
      <div
        className="w-[3.2rem] h-[3.2rem] active:scale-[.98] transition-all p-0 bg-transparent border-0 ml-[.4rem] rounded-full cursor-pointer focus:outline-hidden relative"
        onClick={handleBrackyClick}
        onMouseDown={handleMouseDown}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseLeave}
        onTouchStart={handleMouseDown}
        onTouchEnd={handleMouseUp}
        onTouchCancel={handleMouseLeave}
        onMouseEnter={handleMouseEnter}
        onKeyDown={handleKeyDown}
      >
        <BrackyAvatarImage state={state} />
      </div>
    </div>
  )
})

export default BrackyAvatar
